import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, ChevronRight, PlayCircle, FileText } from 'lucide-react';
import { CourseData, Category, SubCategory, LessonItem } from '../types';

interface CourseSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    courseData: CourseData | null;
    currentLessonId: number;
    onSelectLesson: (index: number) => void;
}

const CourseSidebar: React.FC<CourseSidebarProps> = ({
    isOpen,
    onClose,
    courseData,
    currentLessonId,
    onSelectLesson,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Efecto para hacer scroll automático a la lección activa
    useEffect(() => {
        if (isOpen && courseData) {
            // Esperamos un momento para que terminen las animaciones de apertura (sidebar y acordeones)
            // La animación CSS suele durar 300ms, damos un pequeño buffer extra
            const timer = setTimeout(() => {
                if (scrollContainerRef.current) {
                    // Buscamos el elemento marcado como activo dentro del contenedor
                    const activeElement = scrollContainerRef.current.querySelector('[data-active="true"]');
                    if (activeElement) {
                        activeElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start', // Alinea el elemento en la parte superior del contenedor
                        });
                    }
                }
            }, 400);

            return () => clearTimeout(timer);
        }
    }, [isOpen, currentLessonId, courseData]);

    if (!courseData) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] transition-opacity duration-300 ${
                    isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                onClick={onClose}
            />

            {/* Sidebar Panel */}
            <div
                className={`fixed top-0 left-0 h-full w-full md:w-[450px] bg-[#0a0a0a] border-r border-white/10 z-[70] transform transition-transform duration-300 ease-out flex flex-col ${
                    isOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-white/10 bg-black/20">
                    <div>
                        <h2 className="text-lg font-bold text-white leading-tight mb-1">{courseData.title}</h2>
                        <p className="text-[11px] font-medium text-white/40 uppercase tracking-widest">
                            {courseData.lessons} Lecciones
                        </p>
                    </div>
                    <button
                        title="close"
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content Scrollable */}
                <div
                    ref={scrollContainerRef}
                    className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2 scroll-smooth"
                >
                    {courseData.categories.map((category) => (
                        <CategoryItem
                            key={category.id}
                            category={category}
                            currentLessonId={currentLessonId}
                            onSelectLesson={onSelectLesson}
                        />
                    ))}
                </div>
            </div>
        </>
    );
};

/* --- Componentes Auxiliares para Recursividad y Acordeones --- */

const CategoryItem: React.FC<{
    category: Category;
    currentLessonId: number;
    onSelectLesson: (index: number) => void;
}> = ({ category, currentLessonId, onSelectLesson }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    return (
        <div className="rounded-lg overflow-hidden bg-white/5 border border-white/5 mb-2">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 transition-colors text-left"
            >
                <span className="font-bold text-xs md:text-sm text-indigo-200">{category.title}</span>
                {isExpanded ? (
                    <ChevronDown size={14} className="text-white/50" />
                ) : (
                    <ChevronRight size={14} className="text-white/50" />
                )}
            </button>

            {/* Uso de Grid para transición de altura automática */}
            <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                    isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
            >
                <div className="overflow-hidden">
                    <div className="p-1 space-y-0.5">
                        {category.items.map((item) => {
                            if ('items' in item || 'lessons' in item) {
                                // Es una subcategoría
                                return (
                                    <SubCategoryItem
                                        key={(item as SubCategory).id}
                                        subCategory={item as SubCategory}
                                        currentLessonId={currentLessonId}
                                        onSelectLesson={onSelectLesson}
                                    />
                                );
                            } else {
                                // Es una lección directa
                                return (
                                    <LessonRow
                                        key={(item as LessonItem).uuid}
                                        lesson={item as LessonItem}
                                        isActive={currentLessonId === (item as LessonItem).index}
                                        onClick={() => onSelectLesson((item as LessonItem).index)}
                                    />
                                );
                            }
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

const SubCategoryItem: React.FC<{
    subCategory: SubCategory;
    currentLessonId: number;
    onSelectLesson: (index: number) => void;
}> = ({ subCategory, currentLessonId, onSelectLesson }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Check inicial para abrir si contiene la lección actual
    useEffect(() => {
        const hasActive = subCategory.lessons.some((l) => l.index === currentLessonId);
        if (hasActive) setIsExpanded(true);
    }, [currentLessonId, subCategory.lessons]);

    return (
        <div className="ml-2 pl-2 border-l border-white/10 my-0.5">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-white/5 text-left group"
            >
                {isExpanded ? (
                    <ChevronDown size={12} className="text-white/40 group-hover:text-white" />
                ) : (
                    <ChevronRight size={12} className="text-white/40 group-hover:text-white" />
                )}
                <span className="text-[11px] font-semibold text-white/80 group-hover:text-white">
                    {subCategory.title}
                </span>
            </button>

            {/* Uso de Grid para transición de altura automática */}
            <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                    isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
            >
                <div className="overflow-hidden">
                    <div className="pl-5 pr-1 py-0.5 space-y-0.5">
                        {subCategory.lessons.map((lesson) => (
                            <LessonRow
                                key={lesson.uuid}
                                lesson={lesson}
                                isActive={currentLessonId === lesson.index}
                                onClick={() => onSelectLesson(lesson.index)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const LessonRow: React.FC<{
    lesson: LessonItem;
    isActive: boolean;
    onClick: () => void;
}> = ({ lesson, isActive, onClick }) => {
    return (
        <button
            onClick={onClick}
            data-active={isActive ? 'true' : undefined} // Atributo para que el padre lo encuentre
            className={`w-full flex items-start gap-2.5 p-2 rounded-lg transition-all duration-200 text-left group ${
                isActive ? 'bg-indigo-600/20 border border-indigo-500/30' : 'hover:bg-white/5 border border-transparent'
            }`}
        >
            <div
                className={`mt-0.5 shrink-0 ${isActive ? 'text-indigo-400' : 'text-white/30 group-hover:text-white/60'}`}
            >
                {isActive ? (
                    <div className="relative">
                        <div className="absolute inset-0 bg-indigo-500 blur-sm opacity-50 rounded-full animate-pulse" />
                        <PlayCircle size={16} className="relative z-10" />
                    </div>
                ) : lesson.type === 'content' ? (
                    <FileText size={16} />
                ) : (
                    <PlayCircle size={16} />
                )}
            </div>

            <div className="flex flex-col">
                <span
                    className={`text-xs font-medium leading-snug ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'}`}
                >
                    {lesson.title}
                </span>
            </div>
        </button>
    );
};

export default CourseSidebar;
